import { PageGen, PdfRect, PdfPoint, PdfBrush, PdfColor, PdfUnit, PdfGen } from '../pdf';
import { PDFImage, degrees } from 'pdf-lib';
import { MercatorMap } from './mercatorMap';
import { PointGps } from './pointGps';

/**
 * MapRenderer - renders Mercator maps to PDF pages
 */
export class MapRenderer {
  private pdfImage: PDFImage | null = null;
  private imageLoaded = false;

  constructor(private readonly map: MercatorMap) {}

  /**
   * Preload image into the PDF document for synchronous drawing
   */
  public async preload(pdf: PdfGen): Promise<void> {
    this.pdfImage = await pdf.embedImage(
      `map-${this.map.width}x${this.map.height}`,
      this.map.imageBytes,
      this.map.imageType
    );
    this.imageLoaded = true;
  }

  /**
   * Calculate rotation and scaling parameters
   */
  private rotate(targetRect: PdfRect): {
    rotate: boolean;
    scale: number;
    offsetX: number;
    offsetY: number;
  } {
    const scaleNormal = Math.min(
      targetRect.width / this.map.width,
      targetRect.height / this.map.height
    );
    const scaleRotated = Math.min(
      targetRect.width / this.map.height,
      targetRect.height / this.map.width
    );

    const rotate = scaleRotated > scaleNormal;
    const scale = rotate ? scaleRotated : scaleNormal;

    let offsetX: number;
    let offsetY: number;

    if (rotate) {
      const drawWidth = this.map.height * scale;
      const drawHeight = this.map.width * scale;
      offsetX = (targetRect.width - drawWidth) / 2;
      offsetY = (targetRect.height - drawHeight) / 2;
    } else {
      const drawWidth = this.map.width * scale;
      const drawHeight = this.map.height * scale;
      offsetX = (targetRect.width - drawWidth) / 2;
      offsetY = (targetRect.height - drawHeight) / 2;
    }

    return { rotate, scale, offsetX, offsetY };
  }

  /**
   * Draw map image to the page
   * Uses image caching at PDF document level to avoid embedding same image multiple times
   *
   * Coordinate system: Y increases downward (PdfSharpCore migration)
   * - targetRect.x, targetRect.y are top-left of the target area
   * - We convert to PDF coordinates (Y from bottom) using getXY()
   */
  public drawMap(g: PageGen, targetRect: PdfRect): void {
    if (!this.imageLoaded) {
      throw new Error('Map image not preloaded. Call preload() first.');
    }

    const { rotate, scale, offsetX, offsetY } = this.rotate(targetRect);
    const pdfImage = this.pdfImage!; // Assert non-null after preload

    if (rotate) {
      const drawWidth = this.map.height * scale; // width of rotated bounding box
      const drawHeight = this.map.width * scale; // height of rotated bounding box

      // pdf-lib's rotate(90) rotates counter-clockwise around the bottom-left corner (x, y).
      // To fit into the logical target rect, we need to position the rotation point at the
      // logical bottom-right of the area.
      const pdfX = targetRect.x + offsetX + drawWidth;
      const pdfY = targetRect.y + offsetY + drawHeight;
      const pdfCoords = g.getXY(new PdfPoint(pdfX, pdfY));

      g.page.drawImage(pdfImage, {
        x: pdfCoords.x,
        y: pdfCoords.y,
        width: this.map.width * scale, // original image width
        height: this.map.height * scale, // original image height
        rotate: degrees(90),
      });
    } else {
      const drawWidth = this.map.width * scale;
      const drawHeight = this.map.height * scale;

      // In pdf-lib, non-rotated drawImage expects the bottom-left corner (x, y).
      // We convert the logical bottom-left to PDF coordinates.
      const pdfX = targetRect.x + offsetX;
      const pdfY = targetRect.y + offsetY + drawHeight;
      const pdfCoords = g.getXY(new PdfPoint(pdfX, pdfY));

      g.page.drawImage(pdfImage, {
        x: pdfCoords.x,
        y: pdfCoords.y,
        width: drawWidth,
        height: drawHeight,
      });
    }
  }

  /**
   * Convert GPS coordinates to page coordinates
   */
  public pointFromGps(targetRect: PdfRect, points: PointGps[]): PdfPoint[] {
    const { rotate, scale, offsetX, offsetY } = this.rotate(targetRect);
    const result: PdfPoint[] = [];

    for (const point of points) {
      let { x: px, y: py } = this.map.latLonToPixel(point);

      // Apply scale
      px *= scale;
      py *= scale;

      let pageX: number;
      let pageY: number;

      if (rotate) {
        // Corrected rotation transformation matching C# implementation:
        // pageX = targetRect.X + offsetX + py;
        // pageY = targetRect.Bottom - offsetY - px;
        pageX = targetRect.x + offsetX + py;
        pageY = targetRect.y + targetRect.height - offsetY - px;
      } else {
        pageX = targetRect.x + offsetX + px;
        pageY = targetRect.y + offsetY + py;
      }

      result.push(new PdfPoint(pageX, pageY));
    }

    return result;
  }

  /**
   * Draw points on the map
   */
  public drawPoints(g: PageGen, targetRect: PdfRect, points: PointGps[]): void {
    const { scale } = this.rotate(targetRect);
    const pagePoints = this.pointFromGps(targetRect, points);
    const brush = new PdfBrush(PdfColor.fromRgb(255, 0, 0));
    const r = 4.0;

    for (const point of pagePoints) {
      const rect = new PdfRect(point.x - r / 2, point.y - r / 2, r, r);
      g.drawEllipse(null, brush, rect);
    }
  }

  /**
   * Pick random places ensuring minimum distance between them
   */
  public pickRandomPlaces(
    places: { name: string; gps: PointGps }[],
    count: number,
    targetRect: PdfRect,
    random: { next: (min: number, max: number) => number },
    distanceInCm: number
  ): { name: string; gps: PointGps }[] {
    const result: { name: string; gps: PointGps }[] = [];
    const selected: PdfPoint[] = [];
    const gpsArray = places.map((p) => p.gps);
    const points = this.pointFromGps(targetRect, gpsArray);

    // Convert cm to points (72 points per inch, 2.54 cm per inch)
    const cmToPoints = (72.0 / 2.54) * distanceInCm;

    // Create shuffled indices
    const indices = Array.from({ length: points.length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = random.next(0, i + 1);
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    for (const i of indices) {
      const point = points[i];

      // Check if point is within target rectangle
      if (
        point.x < targetRect.x ||
        point.x > targetRect.right ||
        point.y < targetRect.y ||
        point.y > targetRect.bottom
      ) {
        continue;
      }

      // Check distance from all selected points
      const isValid = selected.every((j) => this.distance(j, point) > cmToPoints);

      if (isValid) {
        result.push(places[i]);
        selected.push(point);
      }

      if (result.length >= count) break;
    }

    return result;
  }

  /**
   * Calculate Euclidean distance between two points
   */
  private distance(p1: PdfPoint, p2: PdfPoint): number {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
